import React from 'react';

import { PROJECTS } from '../../../constants/applicationConstants';
import { listProjects } from '../Services/ProjectService';
import { normalizeProject } from '../Components/ProjectCatalog/projectCatalogUtils';

const FETCH_BATCH_SIZE = 100;
const MAX_FETCH_PAGES = 20;

const useProjectsCatalog = (searchTerm) => {
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState('10');
  const [reportedTotal, setReportedTotal] = React.useState(0);

  const refreshProjects = React.useCallback(async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      let page = 1;
      let total = 0;
      let aggregatedProjects = [];
      let hasMore = true;

      while (hasMore && page <= MAX_FETCH_PAGES) {
        const response = await listProjects({
          limit: FETCH_BATCH_SIZE,
          offset: page,
          filter: [],
          sort: [{ field: 'project.name', dir: 'asc' }],
          moduleName: PROJECTS,
          isServiceWorker: false,
        });

        if (response?.isSuccess === false && !response?.projects) {
          throw new Error(response?.message || 'Failed to load projects.');
        }

        const pageItems = Array.isArray(response?.projects)
          ? response.projects
          : [];
        aggregatedProjects = aggregatedProjects.concat(pageItems);

        const totalFromApi = Number(
          response?.TotalCount ?? aggregatedProjects.length
        );

        total = Number.isFinite(totalFromApi)
          ? totalFromApi
          : aggregatedProjects.length;
        hasMore =
          pageItems.length === FETCH_BATCH_SIZE &&
          aggregatedProjects.length < total;
        page += 1;
      }

      const dedupedProjects = Array.from(
        new Map(
          aggregatedProjects.map((item, index) => {
            const normalized = normalizeProject(item, index);
            return [normalized.key, normalized];
          })
        ).values()
      );

      setProjects(dedupedProjects);
      setReportedTotal(total || dedupedProjects.length);
    } catch (error) {
      setProjects([]);
      setReportedTotal(0);
      setErrorMessage(error?.message || 'Unable to load projects.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  const filteredProjects = React.useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return projects;
    }

    return projects.filter((item) =>
      [item.projectName, item.versionName, item.description, item.status.label]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [projects, searchTerm]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProjects.length / Number(rowsPerPage || 10))
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, searchTerm]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedProjects = React.useMemo(() => {
    const size = Number(rowsPerPage || 10);
    const startIndex = (currentPage - 1) * size;

    return filteredProjects.slice(startIndex, startIndex + size);
  }, [currentPage, filteredProjects, rowsPerPage]);

  const showingFrom = filteredProjects.length
    ? (currentPage - 1) * Number(rowsPerPage || 10) + 1
    : 0;
  const showingTo = filteredProjects.length
    ? Math.min(currentPage * Number(rowsPerPage || 10), filteredProjects.length)
    : 0;

  return {
    errorMessage,
    filteredProjects,
    loading,
    currentPage,
    paginatedProjects,
    refreshProjects,
    reportedTotal,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,
    showingFrom,
    showingTo,
    totalPages,
  };
};

export default useProjectsCatalog;
